/* global bootstrap, Fancybox, Rellax, AOS */

/**
 * Checks whether the user has requested reduced motion at the OS/browser level. Used to skip
 * JS-driven motion (autoplay, parallax) that a CSS-only kill switch can't reach.
 * @returns {boolean} True when the user prefers reduced motion.
 */
function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Calculates the outer height of an element (content + padding + margin).
 * @param {HTMLElement} el Element to measure.
 * @returns {number} Outer height in pixels.
 */
function completeHeight(el) {
    const curStyle = el.currentStyle || window.getComputedStyle(el);
    let outerHeight = el.offsetHeight;
    outerHeight += parseInt(curStyle.marginTop);
    outerHeight += parseInt(curStyle.marginBottom);
    return outerHeight;
}

/**
 * Fixes elements with the `.fix-on-scroll` class to the top of the viewport once the page
 * scrolls past their original position, compensating for the header height.
 * @returns {void}
 */
function fixOnScroll() {
    const allElements = document.querySelectorAll('.fix-on-scroll'),
        windowTop = window.pageYOffset || document.body.scrollTop,
        nav = document.querySelector('#header'),
        navHeight = completeHeight(nav);

    allElements.forEach(el => {
        const elHeight = completeHeight(el);
        if (windowTop > el.offsetTop && windowTop > el.getAttribute('data-offset-top')) {
            el.classList.add('scroll-fixed');
            el.style.top = `${navHeight}px`;
            el.nextElementSibling.style.marginTop = `${elHeight}px`;
        } else {
            el.classList.remove('scroll-fixed');
            el.nextElementSibling.style.marginTop = '0';
        }
    });
}

if (document.querySelector('.fix-on-scroll') !== null) {
    let allElements = document.querySelectorAll('.fix-on-scroll');
    allElements.forEach(el => {
        el.setAttribute('data-offset-top', el.offsetTop);
    });
    window.addEventListener('load', fixOnScroll);
    window.addEventListener('scroll', fixOnScroll);
    window.addEventListener('resize', fixOnScroll);
}

var swiperTranslations = {
    firstSlideMessage: 'Esse é o primeiro slide',
    lastSlideMessage: 'Esse é o último slide',
    nextSlideMessage: 'Próximo Slide',
    paginationBulletMessage: 'Ir para o slide {{index}}',
    prevSlideMessage: 'Slide Anterior'
};

/**
 * Scrolls to an element by id, compensating for the fixed header's height, and moves keyboard
 * focus to it so assistive technology and the next Tab continue from that section.
 * @param {string} id Target element's id, with or without the leading "#".
 * @param {boolean} [smooth=true] Whether to animate the scroll (always instant under reduced motion).
 * @returns {boolean} True when the element exists on this page and was scrolled to.
 */
function scrollToEl(id, smooth = true) {
    if (id[0] !== '#') {
        id = `#${id}`;
    }

    const element = document.querySelector(`${id}`);
    if (element === null) {
        return false;
    }

    const headerCompensate = completeHeight(document.querySelector('#header'));

    element.style.scrollMarginTop = `${headerCompensate + 30}px`;
    element.scrollIntoView({ behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto', block: 'start' });
    if (!element.hasAttribute('tabindex')) {
        element.setAttribute('tabindex', '-1');
    }
    element.focus({ preventScroll: true });
    return true;
}

/**
 * Returns the id of the in-page section the current URL points to: the last path segment (e.g.
 * ".../beneficios") when one of the page's `.scrollTo` links targets it and the section exists.
 * @returns {string|null} The section id, or null when the URL doesn't point to a section.
 */
function sectionIdFromUrl() {
    const segment = decodeURIComponent(window.location.pathname.split('/').filter(Boolean).pop() || '');

    if (segment === '' || document.getElementById(segment) === null) {
        return null;
    }
    const isScrollTarget = [...document.querySelectorAll('.scrollTo')].some(trigger => {
        return (trigger.getAttribute('data-target') || trigger.getAttribute('href')) === segment;
    });
    return isScrollTarget ? segment : null;
}

/**
 * Scrolls to the section the current URL points to (see sectionIdFromUrl()), used when the page is
 * opened directly on a section URL and when the user navigates through the history.
 * @param {boolean} smooth Whether to animate the scroll.
 * @returns {boolean} True when the URL pointed to a section and it was scrolled to.
 */
function scrollToUrlSection(smooth) {
    const id = sectionIdFromUrl();
    return id !== null && scrollToEl(id, smooth);
}

// `.scrollTo` links (bare section ids, e.g. href="contato" — a real route that index.php serves with
// the home page): scroll to the section and update the URL without reloading. When the section is
// not on the current page (header/footer links on the privacy policy or 404 pages), the link is
// followed normally and the home page opens already scrolled to the section.
const buttonScroll = document.querySelectorAll('.scrollTo');
buttonScroll.forEach(trigger => {
    trigger.onclick = e => {
        const id = trigger.getAttribute('data-target') || trigger.getAttribute('href');
        if (!scrollToEl(id)) {
            return;
        }
        e.preventDefault();
        if (trigger.hasAttribute('href') && trigger.href !== window.location.href) {
            window.history.pushState(null, '', trigger.href);
        }
    };
});

window.addEventListener('load', () => {
    scrollToUrlSection(false);
});

window.addEventListener('popstate', () => {
    if (!scrollToUrlSection(true)) {
        window.scrollTo({ top: 0, left: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
});

/**
 * Sets the accessible container message for a Swiper instance and returns the shared
 * translations object used by every Swiper's `a11y` option.
 * @param {string} msg Accessible label describing the specific Swiper container (e.g. "Banner Principal").
 * @returns {object} The shared Swiper a11y translations object, with `containerMessage` set.
 */
// eslint-disable-next-line no-unused-vars -- usado pelos JS de página (inicial, instagram, slides, noticias-single)
function setSwiperMessage(msg) {
    swiperTranslations.containerMessage = msg;
    return swiperTranslations;
}

/**
 * Binds Fancybox to every element with a `data-fancybox` attribute.
 * @returns {void}
 */
function initFancybox() {
    Fancybox.bind('[data-fancybox]', {
        //
    });
}

if (document.querySelector('[data-fancybox]') !== null) {
    initFancybox();
}

/**
 * Enables Bouncer form validation on every `[data-validate]` form, with the project's
 * Portuguese error messages. Forms also marked `[data-ajax]` are validated but not submitted
 * natively (see the comment inside).
 * @returns {void}
 */
function initFormValidation() {
    const bouncerOptions = {
        messages: {
            missingValue: {
                // Um checkbox pode trazer a própria mensagem (ex.: aceite da Política de Privacidade).
                checkbox: field => field.getAttribute('data-bouncer-message') || 'Este campo é obrigatório.',
                radio: 'Por favor selecione uma opção.',
                select: 'Por favor selecione uma opção.',
                'select-multiple': 'Por favor selecione ao menos uma opção.',
                default: 'Por favor preencha este campo.'
            },
            patternMismatch: {
                email: 'Por favor insira um email válido.',
                url: 'Por favor entre uma URL válida.',
                number: 'Por favor entre um número.',
                color: 'Por favor entre com o seguinte formato: #rrggbb',
                date: 'Por favor entre a data no formato DD-MM-YYYY.',
                time: 'Por favor use o formato de 24 horas. Ex.: 23:00',
                month: 'Por favor entre o formato MM-YY',
                default: 'Por favor preencha o campo no formato requerido.'
            },
            outOfRange: {
                over: 'Por favor selecione um valor abaixo de {max}.',
                under: 'Por favor selecione um valor acima de {min}.'
            },
            wrongLength: {
                over: 'O texo não pode passar de {maxLength} caracteres.',
                under: 'O texo precisa ter no mínimo {minLength} caracteres.'
            }
        }
    };

    // Forms com [data-ajax] não fazem o submit nativo quando válidos (o Bouncer só emite
    // `bouncerFormValid`, e o JS da página envia os dados); os demais submetem normalmente.
    // eslint-disable-next-line
    new Bouncer('[data-validate]:not([data-ajax])', bouncerOptions);
    // eslint-disable-next-line
    new Bouncer('[data-validate][data-ajax]', { ...bouncerOptions, disableSubmit: true });
}

if (document.querySelectorAll('[data-validate]').length > 0) {
    initFormValidation();
}

/**
 * Updates the CSS background position of every `.parallax` element based on scroll position,
 * creating a parallax scrolling effect.
 * @returns {void}
 */
function parallax() {
    document.querySelectorAll('.parallax').forEach(e => {
        let yPos = -((window.pageYOffset - e.offsetTop) / e.getAttribute('data-speed'));
        let bgpos = '50% '+ yPos + 'px';
        e.style.backgroundPosition = bgpos;
    });
}

if (document.querySelectorAll('.parallax').length && !prefersReducedMotion()) {
    window.addEventListener('load', parallax);
    window.addEventListener('scroll', parallax);
}

/**
 * Initializes Bootstrap tooltips on every element with a `data-bs-toggle="tooltip"` attribute.
 * @returns {void}
 */
function initTooltips() {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    [...tooltipTriggerList].map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl));
}

if (document.querySelectorAll('[data-bs-toggle="tooltip"]').length > 0) {
    initTooltips();
}

/**
 * Initializes the AOS (Animate On Scroll) library with the project's default options.
 * @returns {void}
 */
function initAOS() {
    AOS.init({
        disable: prefersReducedMotion() ? true : 'mobile',
        once: true
    });
}

initAOS();

/**
 * Initializes the Rellax parallax library on every `.rellax` element.
 * @returns {void}
 */
function initRellax() {
    new Rellax('.rellax', {
        breakpoints: [576, 768, 1200]
    });
}

if (document.querySelector('.rellax') !== null) {
    initRellax();
}

const TRAFFIC_ORIGIN_KEY = 'cm_origem',
    TRAFFIC_ORIGIN_TTL = 90 * 24 * 60 * 60 * 1000,
    TRAFFIC_ORIGIN_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid', 'li_fat_id'];
let currentTrafficOrigin = null;

/**
 * Records where the visitor came from (UTM parameters, ad click ids such as Google Ads' `gclid`, and
 * the external referrer) in localStorage, so the contact form can pass it along even after the
 * visitor navigates inside the site. Last non-direct touch wins: internal navigation and direct
 * visits keep the previously stored origin. Only the referrer's origin + path is kept.
 * @returns {void}
 */
function captureTrafficOrigin() {
    const params = new URLSearchParams(window.location.search),
        touch = {};

    TRAFFIC_ORIGIN_PARAMS.forEach(name => {
        const value = params.get(name);
        if (value) {
            touch[name] = value.slice(0, 200);
        }
    });

    try {
        const referrer = new URL(document.referrer);
        if (referrer.hostname !== window.location.hostname) {
            touch.referrer = referrer.origin + referrer.pathname;
        }
    } catch {
        // Sem referrer (acesso direto) ou referrer inválido.
    }

    if (Object.keys(touch).length === 0) {
        return;
    }

    touch.landing_page = window.location.pathname;
    touch.timestamp = Date.now();
    currentTrafficOrigin = touch;
    try {
        window.localStorage.setItem(TRAFFIC_ORIGIN_KEY, JSON.stringify(touch));
    } catch {
        // Storage bloqueado (aba anônima, cookies desativados): fica só a origem desta página.
    }
}

/**
 * Returns the visitor's traffic origin recorded by captureTrafficOrigin(): the one from this page
 * load when there is one, otherwise the stored one (if younger than 90 days).
 * @returns {Object<string, string|number>} Origin fields (empty object for a direct visit).
 */
// eslint-disable-next-line no-unused-vars
function getTrafficOrigin() {
    if (currentTrafficOrigin !== null) {
        return currentTrafficOrigin;
    }
    try {
        const stored = JSON.parse(window.localStorage.getItem(TRAFFIC_ORIGIN_KEY));
        if (stored && Date.now() - stored.timestamp < TRAFFIC_ORIGIN_TTL) {
            return stored;
        }
    } catch {
        // Storage bloqueado ou valor corrompido.
    }
    return {};
}

captureTrafficOrigin();
